import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useTheme } from '../contexts/ThemeContext';
// Validation patterns matching server-side validation
const NAME_REGEX = /^[A-Za-z\s]+$/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

interface ValidationErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

// Password strength calculation
const calculatePasswordStrength = (password: string): number => {
  if (!password) return 0;

  let score = 0;

  // Length check
  if (password.length >= 12) score += 40;
  else if (password.length >= 8) score += 20;
  else if (password.length >= 5) score += 10;

  // Character variety checks (still reward complexity even if not required)
  if (/[A-Z]/.test(password)) score += 15; // uppercase
  if (/[a-z]/.test(password)) score += 15; // lowercase
  if (/[0-9]/.test(password)) score += 15; // numbers
  if (/[^A-Za-z0-9]/.test(password)) score += 15; // special characters

  return Math.min(100, score);
};

// Get strength label and color based on score
const getPasswordStrengthInfo = (score: number): { label: string; color: string } => {
  if (score >= 80) return { label: 'Very Strong', color: 'bg-green-500' };
  if (score >= 60) return { label: 'Strong', color: 'bg-green-400' };
  if (score >= 40) return { label: 'Medium', color: 'bg-yellow-500' };
  if (score >= 20) return { label: 'Weak', color: 'bg-orange-500' };
  return { label: 'Very Weak', color: 'bg-red-500' };
};

const Register: React.FC = () => {
 // const [name, setName] = useState('');
 const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const navigate = useNavigate();


  // Update password strength score when password changes
  useEffect(() => {
    setPasswordStrength(calculatePasswordStrength(password));
  }, [password]);

  // Validate form fields on input change
  useEffect(() => {
    const errors: ValidationErrors = {};

    // Name validation
    // if (name) {
    //   if (name.length < 2 || name.length > 50) {
    //     errors.name = 'Name must be between 2 and 50 characters';
    //   } else if (!NAME_REGEX.test(name)) {
    //     errors.name = 'Name must contain only letters and spaces';
    //   }
    // }

    // Email validation
    if (email && !EMAIL_REGEX.test(email)) {
      errors.email = 'Please provide a valid email address';
    }

    // Password validation - minimum 12 characters
    if (password && password.length < 12) {
      errors.password = 'Password must be at least 12 characters long';
    }

    // Confirm password validation
    if (confirmPassword && password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);

    // Check if form is valid (all fields have values and no validation errors)
    setIsFormValid(
     // name.trim() !== '' &&
        email.trim() !== '' &&
        password.trim() !== '' &&
        password.length >= 12 &&
        confirmPassword.trim() !== '' &&
        password === confirmPassword
    );
  }, [
   // name, 
    email, password, confirmPassword]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Double-check validation before submission
    if (!isFormValid) {
      setError('Please correct all validation errors before submitting');
      return;
    }

    try {
      const response = await fetch(process.env.REACT_APP_SERVER_URL + "/api/auth/register", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
         // name, 
          email, password, confirmPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
          // Handle validation errors from server
          const errorMessage = data.errors.map((err: any) => err.msg).join(', ');
          setError(errorMessage);
        } else {
          // Use the standardized error message from the response formatter
          setError(data.message || 'An error occurred during registration');
        }
        return;
      }

      // If registration is successful based on standardized successful response
      if (data.success) {
        navigate('./otp');
      } else {
        setError('Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred during registration');
      console.error(err);
    }
  };

  const getInputClasses = (fieldName: keyof ValidationErrors) => {
    return `relative block w-full appearance-none rounded-none ${
      fieldName === 'name' ? 'rounded-t-md' : ''
    } ${fieldName === 'confirmPassword' ? 'rounded-b-md' : ''} border ${
      validationErrors[fieldName]
        ? 'border-red-300 dark:border-red-600'
        : 'border-slate-300 dark:border-slate-600'
    } bg-white dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-50 placeholder-slate-500 dark:placeholder-slate-400 focus:z-10 focus:border-primary-main focus:outline-none focus:ring-primary-main`;
  };

  const { label: strengthLabel, color: strengthColor } = getPasswordStrengthInfo(passwordStrength);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-50">
      <Navbar />
      <div className="flex min-h-[calc(100vh-64px)] items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
       
          
          <div>
          <div className="text-center">
        <img
                src={theme === 'dark' ? 'logo-white.png' : 'logo.png'}
                alt="Logo"
                className="h-12 w-12 mr-2 inline-block"
              />
              </div>
            <h2 className="text-center text-3xl font-bold text-slate-900 dark:text-slate-50">
              Create your account
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-600 p-3 text-red-700 dark:text-red-200">
                {error}
              </div>
            )}
            {/* <div className="-space-y-px rounded-md shadow-sm">
              <div>
                <label htmlFor="name" className="sr-only">
                  Full name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  className={getInputClasses('name')}
                  placeholder="Full name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
                {validationErrors.name && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.name}</p>
                )}
              </div> 
              <div>
                <label htmlFor="email-address" className="sr-only">
                  Email address
                </label>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={getInputClasses('email')}
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                />
                {validationErrors.email && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.email}</p>
                )}
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={getInputClasses('password')}
                  placeholder="Password (at least 12 characters)"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                />
                {password && (
                  <div className="mt-1 mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full">
                        <div
                          className={`h-2 ${strengthColor} rounded-full transition-all duration-300`}
                          style={{ width: `${passwordStrength}%` }}
                        ></div>
                      </div>
                      <span className="text-xs ml-2 min-w-[80px] text-right">{strengthLabel}</span>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      Password must be at least 12 characters long
                    </p>
                  </div>
                )}
                {validationErrors.password && (
                  <p className="text-red-500 text-xs mt-1 ml-1">{validationErrors.password}</p>
                )}
              </div>
              <div>
                <label htmlFor="confirm-password" className="sr-only">
                  Confirm password
                </label>
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className={getInputClasses('confirmPassword')}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                />
                {validationErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1 ml-1">
                    {validationErrors.confirmPassword}
                  </p>
                )}
              </div>
            </div> */}

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              {/* <button
                type="submit"
                disabled={!isFormValid}
                className={`group relative flex w-full justify-center rounded-md border border-transparent ${
                  isFormValid
                    ? 'bg-primary-main hover:bg-primary-dark'
                    : 'bg-primary-main/50 cursor-not-allowed'
                } py-2 px-4 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-primary-main focus:ring-offset-2`}
              >
                Register
              </button> */}
              <h5 style={{color : "white", textAlign : "center"}}>Registration is paused at the moment.</h5>
              <button
                className={`group relative justify-center rounded-md border border-transparent`}
                style={{
                  backgroundColor: "red",
                  marginTop: 15,
                  padding: 3,
                  minWidth: 260,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => window.location.href = "mailto:helpdesk@goldensparrowcapital.com"}
              >
                <h6 style={{ color: "white", textAlign: "center", width: '100%' }}>
                  Email us: helpdesk@goldensparrowcapital.com
                </h6>
              </button>
               {/* <h6 style={{color : "white", textAlign : "center"}}>Email us: helpdesk@goldensparrowcapital.com</h6> */}
              
            </div>
            <div className="text-center text-sm text-slate-600 dark:text-slate-400">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-primary-main hover:text-primary-light">
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
